import React, { useState, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import AppNavbar from "../components/Navbar";
import ConfirmModal from "../components/common/ConfirmModal";
import api from "../services/api";
import toast from "react-hot-toast";
import { HiShieldCheck } from "react-icons/hi";
import "../styles/global.css";
import Loading from "../components/Loading";

const Firewalls = () => {
  const [firewalls, setFirewalls] = useState([]);
  const [droplets, setDroplets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showRuleModal, setShowRuleModal] = useState(false);
  const [showDropletModal, setShowDropletModal] = useState(false);
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: "",
    message: "",
    onConfirm: null,
    variant: "default",
  });
  const [selectedFirewall, setSelectedFirewall] = useState(null);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const initialCreateForm = {
    name: "",
    droplet_ids: [],
  };
  const [formData, setFormData] = useState(initialCreateForm);
  const [editFormData, setEditFormData] = useState({
    name: "",
  });
  const initialRuleForm = {
    type: "inbound", // inbound or outbound
    protocol: "tcp",
    ports: "all",
    addresses: ["0.0.0.0/0", "::/0"],
  };
  const [newRule, setNewRule] = useState(initialRuleForm);

  const resetCreateForm = () => setFormData({ ...initialCreateForm });
  const resetRuleForm = () => setNewRule({ ...initialRuleForm });

  const handleCloseCreateModal = () => {
    if (creating) return;
    setShowCreateModal(false);
    resetCreateForm();
  };

  const handleCloseEditModal = () => {
    if (updating) return;
    setShowEditModal(false);
    setEditFormData({ name: "" });
    setSelectedFirewall(null);
  };

  const handleCloseDetailsModal = () => {
    setShowDetailsModal(false);
    setSelectedFirewall(null);
  };

  const handleCloseRuleModal = () => {
    if (updating) return;
    setShowRuleModal(false);
    resetRuleForm();
  };

  const handleCloseDropletModal = () => {
    if (updating) return;
    setShowDropletModal(false);
  };

  useEffect(() => {
    fetchFirewalls();
    fetchDroplets();
  }, []);

  const fetchFirewalls = async () => {
    try {
      setLoading(true);
      const response = await api.get("/firewalls");
      setFirewalls(response.data || []);
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.response?.data?.error ||
        "Failed to load firewalls";
      toast.error(errorMessage);
      setFirewalls([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchDroplets = async () => {
    try {
      const response = await api.get("/droplets");
      setDroplets(response.data || []);
    } catch (err) {
      console.error("Failed to load droplets:", err);
    }
  };

  const fetchFirewallDetails = async (firewallId) => {
    try {
      const response = await api.get(`/firewalls/${firewallId}`);
      return response.data;
    } catch (err) {
      toast.error("Failed to load firewall details");
      return null;
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error("Firewall name is required");
      return;
    }

    setCreating(true);
    try {
      await api.post("/firewalls", {
        name: formData.name.trim(),
        inbound_rules: [],
        outbound_rules: [],
        droplet_ids: formData.droplet_ids || [],
      });
      toast.success("Firewall created successfully");
      setShowCreateModal(false);
      resetCreateForm();
      await fetchFirewalls();
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.response?.data?.error ||
        "Failed to create firewall";
      toast.error(errorMessage);
    } finally {
      setCreating(false);
    }
  };

  const handleEdit = async (firewall) => {
    const details = await fetchFirewallDetails(firewall.id);
    if (details) {
      setSelectedFirewall(details);
      setEditFormData({ name: details.name });
      setShowEditModal(true);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editFormData.name.trim()) {
      toast.error("Firewall name is required");
      return;
    }

    setUpdating(true);
    try {
      const details = await fetchFirewallDetails(selectedFirewall.id);
      await api.put(`/firewalls/${selectedFirewall.id}`, {
        name: editFormData.name.trim(),
        inbound_rules: details.inbound_rules || [],
        outbound_rules: details.outbound_rules || [],
      });
      toast.success("Firewall updated successfully");
      setShowEditModal(false);
      await fetchFirewalls();
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.response?.data?.error ||
        "Failed to update firewall";
      toast.error(errorMessage);
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = (firewallId, firewallName) => {
    setConfirmModal({
      isOpen: true,
      title: "Delete Firewall",
      message: `Are you sure you want to delete the firewall "${firewallName}"? This action cannot be undone.`,
      onConfirm: async () => {
        setDeleting(firewallId);
        try {
          await api.delete(`/firewalls/${firewallId}`);
          toast.success("Firewall deleted successfully");
          await fetchFirewalls();
        } catch (err) {
          const errorMessage =
            err.response?.data?.message ||
            err.response?.data?.error ||
            "Failed to delete firewall";
          toast.error(errorMessage);
        } finally {
          setDeleting(null);
          setConfirmModal({
            isOpen: false,
            title: "",
            message: "",
            onConfirm: null,
            variant: "default",
          });
        }
      },
      variant: "danger",
    });
  };

  const handleViewDetails = async (firewall) => {
    const details = await fetchFirewallDetails(firewall.id);
    if (details) {
      setSelectedFirewall(details);
      setShowDetailsModal(true);
    }
  };

  const handleAddRule = (firewall, type) => {
    setSelectedFirewall(firewall);
    setNewRule({
      ...initialRuleForm,
      type,
    });
    setShowRuleModal(true);
  };

  const handleSaveRule = async () => {
    if (!newRule.addresses || newRule.addresses.length === 0) {
      toast.error("At least one address is required");
      return;
    }

    setUpdating(true);
    try {
      const details = await fetchFirewallDetails(selectedFirewall.id);
      const inboundRules = [...(details.inbound_rules || [])];
      const outboundRules = [...(details.outbound_rules || [])];

      const rule = {
        protocol: newRule.protocol,
        ports: newRule.ports,
      };

      if (newRule.type === "inbound") {
        rule.sources = { addresses: newRule.addresses };
        inboundRules.push(rule);
      } else {
        rule.destinations = { addresses: newRule.addresses };
        outboundRules.push(rule);
      }

      await api.put(`/firewalls/${selectedFirewall.id}`, {
        name: details.name,
        inbound_rules: inboundRules,
        outbound_rules: outboundRules,
      });

      toast.success("Rule added successfully");
      setShowRuleModal(false);
      resetRuleForm();
      await fetchFirewalls();
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.response?.data?.error ||
        "Failed to add rule";
      toast.error(errorMessage);
    } finally {
      setUpdating(false);
    }
  };

  const handleRemoveRule = (firewall, ruleType, ruleIndex) => {
    setConfirmModal({
      isOpen: true,
      title: "Remove Rule",
      message: "Are you sure you want to remove this rule?",
      onConfirm: async () => {
        setUpdating(true);
        try {
          const details = await fetchFirewallDetails(firewall.id);
          const inboundRules = [...(details.inbound_rules || [])];
          const outboundRules = [...(details.outbound_rules || [])];

          if (ruleType === "inbound") {
            inboundRules.splice(ruleIndex, 1);
          } else {
            outboundRules.splice(ruleIndex, 1);
          }

          // Ensure at least one rule exists
          if (inboundRules.length === 0 && outboundRules.length === 0) {
            toast.error("At least one rule is required");
            setConfirmModal({
              isOpen: false,
              title: "",
              message: "",
              onConfirm: null,
              variant: "default",
            });
            setUpdating(false);
            return;
          }

          await api.put(`/firewalls/${firewall.id}`, {
            name: details.name,
            inbound_rules: inboundRules,
            outbound_rules: outboundRules,
          });

          toast.success("Rule removed successfully");
          await fetchFirewalls();
        } catch (err) {
          const errorMessage =
            err.response?.data?.message ||
            err.response?.data?.error ||
            "Failed to remove rule";
          toast.error(errorMessage);
        } finally {
          setUpdating(false);
          setConfirmModal({
            isOpen: false,
            title: "",
            message: "",
            onConfirm: null,
            variant: "default",
          });
        }
      },
      variant: "danger",
    });
  };

  const handleManageDroplets = async (firewall) => {
    const details = await fetchFirewallDetails(firewall.id);
    if (details) {
      setSelectedFirewall(details);
      setFormData({ name: "", droplet_ids: details.droplet_ids || [] });
      setShowDropletModal(true);
    }
  };

  const handleSaveDroplets = async () => {
    setUpdating(true);
    try {
      const currentDroplets = selectedFirewall.droplet_ids || [];
      const newDroplets = formData.droplet_ids || [];
      const toAdd = newDroplets.filter((id) => !currentDroplets.includes(id));
      const toRemove = currentDroplets.filter(
        (id) => !newDroplets.includes(id)
      );

      if (toAdd.length > 0) {
        await api.post(`/firewalls/${selectedFirewall.id}/droplets`, {
          droplet_ids: toAdd,
        });
      }

      if (toRemove.length > 0) {
        await api.delete(`/firewalls/${selectedFirewall.id}/droplets`, {
          data: { droplet_ids: toRemove },
        });
      }

      toast.success("Droplets updated successfully");
      setShowDropletModal(false);
      await fetchFirewalls();
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.response?.data?.error ||
        "Failed to update droplets";
      toast.error(errorMessage);
    } finally {
      setUpdating(false);
    }
  };

  const handleDropletToggle = (dropletId) => {
    setFormData((prev) => {
      const currentIds = prev.droplet_ids || [];
      if (currentIds.includes(dropletId)) {
        return {
          ...prev,
          droplet_ids: currentIds.filter((id) => id !== dropletId),
        };
      } else {
        return {
          ...prev,
          droplet_ids: [...currentIds, dropletId],
        };
      }
    });
  };

  const formatRuleAddresses = (rule) => {
    const addresses =
      rule.sources?.addresses || rule.destinations?.addresses || [];
    return addresses.join(", ") || "—";
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdown && !event.target.closest(".space-dropdown-wrapper")) {
        setOpenDropdown(null);
      }
    };

    if (openDropdown) {
      document.addEventListener("click", handleClickOutside);
      return () => document.removeEventListener("click", handleClickOutside);
    }
  }, [openDropdown]);

  if (loading && firewalls.length === 0) {
    return (
      <>
        <AppNavbar />
        <div className="container">
          <Loading text="Loading firewalls..." className="main-container" />
        </div>
      </>
    );
  }

  return (
    <>
      <AppNavbar />
      <div className="container">
        <div className="page-header main-container">
          <div>
            <h1 className="page-title">Firewalls</h1>
            <p className="page-subtitle">
              Manage network security rules for your droplets
            </p>
          </div>
          <button
            className="btn-primary btn-cta"
            onClick={() => setShowCreateModal(true)}
          >
            + Create Firewall
          </button>
        </div>

        {firewalls.length === 0 ? (
          <div className="empty-state-card">
            <div className="empty-icon">
              <HiShieldCheck />
            </div>
            <h2 className="empty-title">No firewalls created yet</h2>
            <p className="empty-desc">
              Create a firewall to add network security rules to your droplets.
            </p>
            <button
              className="btn-secondary mt-3"
              onClick={() => setShowCreateModal(true)}
            >
              Create Your First Firewall
            </button>
          </div>
        ) : (
          <div className="space-table-container">
            <table className="space-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Inbound Rules</th>
                  <th>Outbound Rules</th>
                  <th>Droplets</th>
                  <th className="actions-column">Actions</th>
                </tr>
              </thead>
              <tbody>
                {firewalls.map((fw) => (
                  <tr key={fw.id} className="space-table-row">
                    <td>
                      <strong>{fw.name}</strong>
                    </td>
                    <td>{fw.inbound_rules?.length || 0}</td>
                    <td>{fw.outbound_rules?.length || 0}</td>
                    <td>
                      {fw.droplet_ids?.length > 0 ? (
                        <span className="badge badge-info">
                          {fw.droplet_ids.length} droplet
                          {fw.droplet_ids.length !== 1 ? "s" : ""}
                        </span>
                      ) : (
                        <span className="text-muted">None</span>
                      )}
                    </td>
                    <td className="actions-column">
                      <div className="space-dropdown-wrapper">
                        <button
                          className="space-dropdown-trigger"
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenDropdown(
                              openDropdown === fw.id ? null : fw.id
                            );
                          }}
                        >
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <circle cx="12" cy="12" r="1" />
                            <circle cx="12" cy="5" r="1" />
                            <circle cx="12" cy="19" r="1" />
                          </svg>
                        </button>
                        {openDropdown === fw.id && (
                          <div className="space-dropdown-menu">
                            <button
                              className="custom-dropdown-item"
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenDropdown(null);
                                handleViewDetails(fw);
                              }}
                            >
                              <span>View Details</span>
                            </button>
                            <button
                              className="custom-dropdown-item"
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenDropdown(null);
                                handleEdit(fw);
                              }}
                            >
                              <span>Edit Name</span>
                            </button>
                            <button
                              className="custom-dropdown-item"
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenDropdown(null);
                                handleManageDroplets(fw);
                              }}
                            >
                              <span>Manage Droplets</span>
                            </button>
                            <button
                              className="custom-dropdown-item danger"
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenDropdown(null);
                                handleDelete(fw.id, fw.name);
                              }}
                              disabled={deleting === fw.id}
                            >
                              <span>
                                {deleting === fw.id ? "Deleting..." : "Delete"}
                              </span>
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Create Firewall Modal */}
        <Modal
          show={showCreateModal}
          onHide={handleCloseCreateModal}
          centered
          size="lg"
        >
          <Modal.Header closeButton={!creating}>
            <Modal.Title>Create Firewall</Modal.Title>
          </Modal.Header>
          <form onSubmit={handleCreate}>
            <Modal.Body>
              <div className="space-form-group">
                <label className="form-label">Firewall Name</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="e.g., production-firewall, web-server-fw"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  required
                  disabled={creating}
                  autoFocus
                />
                <p className="text-muted mt-1">
                  Choose a descriptive name for your firewall
                </p>
              </div>

              <div className="space-form-group">
                <label className="form-label">
                  Assign to Droplets (Optional)
                </label>
                <p className="text-muted mb-2">
                  You can assign this firewall to droplets now or later. The
                  firewall will have default outbound rules allowing all
                  traffic.
                </p>
                {droplets.length === 0 ? (
                  <p className="text-muted">
                    No droplets available. Create a droplet first.
                  </p>
                ) : (
                  <div className="firewall-droplet-list">
                    {droplets.map((droplet) => (
                      <label
                        key={droplet.id}
                        className="checkbox-label firewall-droplet-item"
                      >
                        <input
                          type="checkbox"
                          checked={
                            formData.droplet_ids?.includes(
                              droplet.droplet_id
                            ) || false
                          }
                          onChange={() =>
                            handleDropletToggle(droplet.droplet_id)
                          }
                          disabled={creating}
                        />
                        <span>
                          <strong>{droplet.name}</strong>
                          <span className="text-muted">
                            {" "}
                            ({droplet.ip_address || "No IP"})
                          </span>
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              <div className="firewall-info-alert">
                <p>
                  <strong>Note:</strong> This firewall will be created with
                  default outbound rules (TCP, UDP, ICMP) allowing all traffic.
                  You can customize rules after creation.
                </p>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <button
                type="button"
                className="btn-secondary"
                onClick={handleCloseCreateModal}
                disabled={creating}
              >
                Cancel
              </button>
              <button type="submit" className="btn-primary" disabled={creating}>
                {creating ? "Creating..." : "Create Firewall"}
              </button>
            </Modal.Footer>
          </form>
        </Modal>

        {/* Edit Firewall Modal */}
        {showEditModal && selectedFirewall && (
          <Modal
            show={showEditModal}
            onHide={handleCloseEditModal}
            size="lg"
            centered
          >
            <Modal.Header closeButton={!updating}>
              <Modal.Title>Edit Firewall</Modal.Title>
            </Modal.Header>
            <form onSubmit={handleUpdate}>
              <Modal.Body>
                <div className="space-form-group">
                  <label className="form-label">Firewall Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={editFormData.name}
                    onChange={(e) =>
                      setEditFormData({
                        ...editFormData,
                        name: e.target.value,
                      })
                    }
                    required
                    disabled={updating}
                    autoFocus
                  />
                </div>
              </Modal.Body>
              <Modal.Footer>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={handleCloseEditModal}
                  disabled={updating}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={updating || !editFormData.name.trim()}
                >
                  {updating ? "Updating..." : "Update"}
                </button>
              </Modal.Footer>
            </form>
          </Modal>
        )}

        {/* Firewall Details Modal */}
        {showDetailsModal && selectedFirewall && (
          <Modal
            show={showDetailsModal}
            onHide={handleCloseDetailsModal}
            size="lg"
            centered
          >
            <Modal.Header closeButton>
              <Modal.Title>
                Firewall Details: {selectedFirewall.name}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div className="space-settings-section">
                <div className="space-settings-header">
                  <div className="space-settings-header-content">
                    <h3 className="space-settings-title">Inbound Rules</h3>
                    <p className="space-settings-desc">
                      Rules that control incoming traffic to your droplets
                    </p>
                  </div>
                  <button
                    className="btn-primary"
                    onClick={() => handleAddRule(selectedFirewall, "inbound")}
                  >
                    + Add Rule
                  </button>
                </div>
                <div className="firewall-rule-list">
                  {selectedFirewall.inbound_rules?.length > 0 ? (
                    selectedFirewall.inbound_rules.map((rule, index) => (
                      <div key={index} className="firewall-rule-item">
                        <div className="firewall-rule-details">
                          <span className="firewall-rule-protocol">
                            {rule.protocol.toUpperCase()}
                          </span>
                          <span className="firewall-rule-ports">
                            {rule.ports || "all"}
                          </span>
                          <span className="firewall-rule-addresses">
                            from {formatRuleAddresses(rule)}
                          </span>
                        </div>
                        <button
                          className="space-action-btn danger"
                          onClick={() =>
                            handleRemoveRule(selectedFirewall, "inbound", index)
                          }
                        >
                          Remove
                        </button>
                      </div>
                    ))
                  ) : (
                    <p className="text-muted">No inbound rules configured</p>
                  )}
                </div>
              </div>

              <div className="space-settings-section">
                <div className="space-settings-header">
                  <div className="space-settings-header-content">
                    <h3 className="space-settings-title">Outbound Rules</h3>
                    <p className="space-settings-desc">
                      Rules that control outgoing traffic from your droplets
                    </p>
                  </div>
                  <button
                    className="btn-primary"
                    onClick={() => handleAddRule(selectedFirewall, "outbound")}
                  >
                    + Add Rule
                  </button>
                </div>
                <div className="firewall-rule-list">
                  {selectedFirewall.outbound_rules?.length > 0 ? (
                    selectedFirewall.outbound_rules.map((rule, index) => (
                      <div key={index} className="firewall-rule-item">
                        <div className="firewall-rule-details">
                          <span className="firewall-rule-protocol">
                            {rule.protocol.toUpperCase()}
                          </span>
                          <span className="firewall-rule-ports">
                            {rule.ports || "all"}
                          </span>
                          <span className="firewall-rule-addresses">
                            to {formatRuleAddresses(rule)}
                          </span>
                        </div>
                        <button
                          className="space-action-btn danger"
                          onClick={() =>
                            handleRemoveRule(
                              selectedFirewall,
                              "outbound",
                              index
                            )
                          }
                        >
                          Remove
                        </button>
                      </div>
                    ))
                  ) : (
                    <p className="text-muted">No outbound rules configured</p>
                  )}
                </div>
              </div>

              <div className="space-settings-section">
                <div className="space-settings-header">
                  <div className="space-settings-header-content">
                    <h3 className="space-settings-title">Assigned Droplets</h3>
                    <p className="space-settings-desc">
                      Droplets that have this firewall applied
                    </p>
                  </div>
                  <button
                    className="btn-primary"
                    onClick={() => handleManageDroplets(selectedFirewall)}
                  >
                    Manage Droplets
                  </button>
                </div>
                <div className="space-settings-content">
                  {selectedFirewall.droplet_ids?.length > 0 ? (
                    <div className="firewall-droplet-list">
                      {selectedFirewall.droplet_ids.map((dropletId) => {
                        const droplet = droplets.find(
                          (d) => d.droplet_id === dropletId
                        );
                        return droplet ? (
                          <div
                            key={dropletId}
                            className="firewall-droplet-item"
                          >
                            <strong>{droplet.name}</strong>
                            <span className="text-muted">
                              {" "}
                              ({droplet.ip_address || "No IP"})
                            </span>
                          </div>
                        ) : (
                          <div
                            key={dropletId}
                            className="firewall-droplet-item"
                          >
                            <span className="text-muted">
                              Droplet ID: {dropletId}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-muted">No droplets assigned</p>
                  )}
                </div>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <button
                className="btn-secondary"
                onClick={handleCloseDetailsModal}
              >
                Close
              </button>
            </Modal.Footer>
          </Modal>
        )}

        {/* Add Rule Modal */}
        {showRuleModal && selectedFirewall && (
          <Modal
            show={showRuleModal}
            onHide={handleCloseRuleModal}
            size="lg"
            centered
          >
            <Modal.Header closeButton={!updating}>
              <Modal.Title>
                Add {newRule.type === "inbound" ? "Inbound" : "Outbound"} Rule
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div className="space-form-group">
                <label className="form-label">Protocol</label>
                <select
                  className="form-control"
                  value={newRule.protocol}
                  onChange={(e) =>
                    setNewRule({ ...newRule, protocol: e.target.value })
                  }
                  disabled={updating}
                >
                  <option value="tcp">TCP</option>
                  <option value="udp">UDP</option>
                  <option value="icmp">ICMP</option>
                </select>
              </div>
              <div className="space-form-group">
                <label className="form-label">Ports</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="e.g., 80, 443, 8080 or 'all'"
                  value={newRule.ports}
                  onChange={(e) =>
                    setNewRule({ ...newRule, ports: e.target.value })
                  }
                  disabled={updating}
                />
                <p className="text-muted mt-1">
                  Enter port number(s) separated by commas, or 'all' for all
                  ports
                </p>
              </div>
              <div className="space-form-group">
                <label className="form-label">
                  {newRule.type === "inbound" ? "Source" : "Destination"}{" "}
                  Addresses
                </label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="e.g., 0.0.0.0/0, ::/0, 192.168.1.0/24"
                  value={newRule.addresses.join(", ")}
                  onChange={(e) =>
                    setNewRule({
                      ...newRule,
                      addresses: e.target.value
                        .split(",")
                        .map((s) => s.trim())
                        .filter((s) => s),
                    })
                  }
                  disabled={updating}
                />
                <p className="text-muted mt-1">
                  Enter IP addresses or CIDR ranges separated by commas
                </p>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <button
                type="button"
                className="btn-secondary"
                onClick={handleCloseRuleModal}
                disabled={updating}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={handleSaveRule}
                disabled={
                  updating ||
                  !newRule.addresses ||
                  newRule.addresses.length === 0
                }
              >
                {updating ? "Adding..." : "Add Rule"}
              </button>
            </Modal.Footer>
          </Modal>
        )}

        {/* Manage Droplets Modal */}
        {showDropletModal && selectedFirewall && (
          <Modal
            show={showDropletModal}
            onHide={handleCloseDropletModal}
            size="lg"
            centered
          >
            <Modal.Header closeButton={!updating}>
              <Modal.Title>Manage Droplets</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div className="space-form-group">
                <label className="form-label">
                  Assign Firewall to Droplets
                </label>
                <p className="text-muted mb-2">
                  Select droplets to apply this firewall to
                </p>
                {droplets.length === 0 ? (
                  <p className="text-muted">No droplets available</p>
                ) : (
                  <div className="firewall-droplet-list">
                    {droplets.map((droplet) => (
                      <label
                        key={droplet.id}
                        className="checkbox-label firewall-droplet-item"
                      >
                        <input
                          type="checkbox"
                          checked={
                            formData.droplet_ids?.includes(
                              droplet.droplet_id
                            ) || false
                          }
                          onChange={() =>
                            handleDropletToggle(droplet.droplet_id)
                          }
                          disabled={updating}
                        />
                        <span>
                          <strong>{droplet.name}</strong>
                          <span className="text-muted">
                            {" "}
                            ({droplet.ip_address || "No IP"})
                          </span>
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </Modal.Body>
            <Modal.Footer>
              <button
                type="button"
                className="btn-secondary"
                onClick={handleCloseDropletModal}
                disabled={updating}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={handleSaveDroplets}
                disabled={updating}
              >
                {updating ? "Saving..." : "Save Changes"}
              </button>
            </Modal.Footer>
          </Modal>
        )}

        {/* Confirm Modal */}
        <ConfirmModal
          show={confirmModal.isOpen}
          onClose={() =>
            setConfirmModal({
              isOpen: false,
              title: "",
              message: "",
              onConfirm: null,
              variant: "default",
            })
          }
          onConfirm={confirmModal.onConfirm || (() => {})}
          title={confirmModal.title}
          message={confirmModal.message}
          confirmText={confirmModal.variant === "danger" ? "Delete" : "Confirm"}
          cancelText="Cancel"
          variant={confirmModal.variant}
        />
      </div>
    </>
  );
};

export default Firewalls;
